/**
 * usePageFlipSound - Premium Page Flip Sound Hook
 *
 * 부드러운 "쓰~윽" 소리로 실감나는 책 넘김 경험
 * Smooth "swish~" sound for realistic page turning
 *
 * 弘益人間 - WIA Standards
 */

import { useRef, useCallback, useEffect } from 'react';

interface UsePageFlipSoundOptions {
  enabled?: boolean;
  volume?: number;
}

interface UsePageFlipSoundReturn {
  playFlipSound: () => void;
  playHoverSound: () => void;
  playPageTurnStart: () => void;
  playPageTurnEnd: () => void;
  isEnabled: boolean;
}

export function usePageFlipSound(
  options: UsePageFlipSoundOptions = {}
): UsePageFlipSoundReturn {
  const { enabled = true, volume = 0.4 } = options;
  const audioContextRef = useRef<AudioContext | null>(null);

  // Initialize AudioContext on first interaction
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    // Resume if suspended
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  // 부드러운 페이지 넘김 소리 (1.2초, 부드럽게 "쓰~윽")
  const playFlipSound = useCallback(() => {
    if (!enabled) return;

    try {
      const ctx = getAudioContext();
      const now = ctx.currentTime;
      const duration = 1.2; // 1.2초로 늘림

      // === Part 1: Soft paper rustle (Pink noise) ===
      const bufferSize = Math.floor(ctx.sampleRate * duration);
      const noiseBuffer = ctx.createBuffer(2, bufferSize, ctx.sampleRate);

      // Pink noise generation (softer than white noise)
      for (let channel = 0; channel < 2; channel++) {
        const data = noiseBuffer.getChannelData(channel);
        let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;

        for (let i = 0; i < bufferSize; i++) {
          const white = Math.random() * 2 - 1;
          b0 = 0.99886 * b0 + white * 0.0555179;
          b1 = 0.99332 * b1 + white * 0.0750759;
          b2 = 0.96900 * b2 + white * 0.1538520;
          b3 = 0.86650 * b3 + white * 0.3104856;
          b4 = 0.55000 * b4 + white * 0.5329522;
          b5 = -0.7616 * b5 - white * 0.0168980;
          data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.08; // 더 부드럽게
          b6 = white * 0.115926;
        }
      }

      const noiseSource = ctx.createBufferSource();
      noiseSource.buffer = noiseBuffer;

      // Bandpass for paper texture - 낮은 주파수로 부드럽게
      const bandpass = ctx.createBiquadFilter();
      bandpass.type = 'bandpass';
      bandpass.frequency.value = 900; // 더 낮춤
      bandpass.Q.value = 0.6;

      // Highpass to remove rumble
      const highpass = ctx.createBiquadFilter();
      highpass.type = 'highpass';
      highpass.frequency.value = 300;

      // Envelope: very slow attack, long sustain, smooth decay
      const envelope = ctx.createGain();
      envelope.gain.setValueAtTime(0, now);
      envelope.gain.linearRampToValueAtTime(volume * 0.5, now + 0.2); // 더 천천히
      envelope.gain.setValueAtTime(volume * 0.5, now + 0.4);
      envelope.gain.linearRampToValueAtTime(volume * 0.35, now + 0.7);
      envelope.gain.exponentialRampToValueAtTime(0.001, now + duration);

      // === Part 2: Very soft swish sweep ===
      const swish = ctx.createOscillator();
      swish.type = 'sine';
      swish.frequency.setValueAtTime(200, now); // 더 낮춤
      swish.frequency.exponentialRampToValueAtTime(60, now + duration * 0.8);

      const swishGain = ctx.createGain();
      swishGain.gain.setValueAtTime(0, now);
      swishGain.gain.linearRampToValueAtTime(volume * 0.05, now + 0.15);
      swishGain.gain.exponentialRampToValueAtTime(0.001, now + duration * 0.7);

      // Connect nodes
      noiseSource.connect(bandpass);
      bandpass.connect(highpass);
      highpass.connect(envelope);
      envelope.connect(ctx.destination);

      swish.connect(swishGain);
      swishGain.connect(ctx.destination);

      // Play all
      noiseSource.start(now);
      swish.start(now);

      noiseSource.stop(now + duration);
      swish.stop(now + duration);
    } catch (e) {
      // Audio not supported
    }
  }, [enabled, volume, getAudioContext]);

  // 모서리 호버 소리 (짧고 부드러운 "쓱")
  const playHoverSound = useCallback(() => {
    if (!enabled) return;

    try {
      const ctx = getAudioContext();
      const now = ctx.currentTime;
      const duration = 0.35; // 약간 늘림

      // Soft crinkle
      const bufferSize = Math.floor(ctx.sampleRate * duration);
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);

      let lastOut = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        data[i] = (lastOut + 0.012 * white) / 1.012; // 더 부드럽게
        lastOut = data[i];
        data[i] *= 1.5;
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 1400; // 낮춤
      filter.Q.value = 0.8;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(volume * 0.15, now + 0.08); // 볼륨 낮춤
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

      source.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      source.start(now);
      source.stop(now + duration);
    } catch (e) {
      // Audio not supported
    }
  }, [enabled, volume, getAudioContext]);

  // 페이지 넘김 시작 소리
  const playPageTurnStart = useCallback(() => {
    if (!enabled) return;

    try {
      const ctx = getAudioContext();
      const now = ctx.currentTime;

      // Quick lift sound
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(200, now);
      osc.frequency.exponentialRampToValueAtTime(400, now + 0.1);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(volume * 0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.15);
    } catch (e) {
      // Audio not supported
    }
  }, [enabled, volume, getAudioContext]);

  // 페이지 넘김 완료 소리 (착지)
  const playPageTurnEnd = useCallback(() => {
    if (!enabled) return;

    try {
      const ctx = getAudioContext();
      const now = ctx.currentTime;

      // Soft thud
      const bufferSize = Math.floor(ctx.sampleRate * 0.1);
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);

      for (let i = 0; i < bufferSize; i++) {
        const t = i / ctx.sampleRate;
        data[i] = Math.sin(2 * Math.PI * 80 * t) * Math.exp(-t * 30);
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      const gain = ctx.createGain();
      gain.gain.value = volume * 0.2;

      source.connect(gain);
      gain.connect(ctx.destination);

      source.start(now);
      source.stop(now + 0.1);
    } catch (e) {
      // Audio not supported
    }
  }, [enabled, volume, getAudioContext]);

  return {
    playFlipSound,
    playHoverSound,
    playPageTurnStart,
    playPageTurnEnd,
    isEnabled: enabled,
  };
}

export default usePageFlipSound;
